import { useEffect, useMemo, useRef, useState } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'
import QRCode from 'qrcode'
import DownloadHero from '../components/m1nt/download/DownloadHero'
import { AppWindow, PhoneFrame } from '../components/m1nt/download/DeviceFrames'
import { M1ntButton } from '../components/m1nt/ui'
import { promptInstall } from '../pwa/pwaState'
import { getInstallGuide, usePwa } from '../pwa/usePwa'

const PRODUCTION_URL = 'https://m1nt-owner.web.app'

/** Address the phone should open. Local and private addresses cannot be reached from a phone. */
function getShareUrl() {
  const fromEnv = import.meta.env.VITE_PUBLIC_APP_URL
  if (fromEnv) return String(fromEnv).replace(/\/$/, '')
  if (typeof window === 'undefined') return PRODUCTION_URL
  const { hostname, origin } = window.location
  const isPrivateHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.local') ||
    /^(10|192\.168|172\.(1[6-9]|2\d|3[01]))\./.test(hostname)
  return isPrivateHost ? PRODUCTION_URL : origin
}

/** QR code drawn as one SVG path, so no markup is injected. */
function QrCode({ value, label }) {
  const { size, path } = useMemo(() => {
    const qr = QRCode.create(value, { errorCorrectionLevel: 'M' })
    const modules = qr.modules
    let d = ''
    for (let row = 0; row < modules.size; row += 1) {
      for (let col = 0; col < modules.size; col += 1) {
        if (modules.get(row, col)) d += `M${col} ${row}h1v1h-1z`
      }
    }
    return { size: modules.size, path: d }
  }, [value])

  return (
    <svg
      viewBox={`-2 -2 ${size + 4} ${size + 4}`}
      role="img"
      aria-label={label}
      shapeRendering="crispEdges"
      className="block h-full w-full"
    >
      <rect x="-2" y="-2" width={size + 4} height={size + 4} fill="#fff" />
      <path d={path} fill="#0A090D" />
    </svg>
  )
}

const STEPS = {
  iphone: {
    title: 'iPhone and iPad',
    steps: [
      'Open the link in Safari.',
      'Tap the Share button.',
      'Choose "Add to Home Screen", then tap Add.',
    ],
  },
  android: {
    title: 'Android phones and tablets',
    steps: [
      'Open the link in Chrome.',
      'Tap the menu (three dots).',
      'Choose "Install app" or "Add to Home screen".',
    ],
  },
}

const EASE = [0.16, 1, 0.3, 1]

const COMPUTER_FEATURES = [
  {
    title: 'Its own window',
    text: 'No tabs and no address bar. M1NT opens on its own, like any other app on your computer.',
  },
  {
    title: 'One click away',
    text: 'Pin it to your dock or taskbar and land straight on My Cards.',
  },
  {
    title: 'Quietly up to date',
    text: 'New versions download in the background. You choose when to reload.',
  },
]

/** Fades and lifts its content once, when it scrolls into view. */
function Reveal({ children, delay = 0, className = '' }) {
  const reduceMotion = useReducedMotion()
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  )
}

/** Moves its content a little against the scroll, so the devices float above the page. */
function Drift({ children, from = 36, to = -36, className = '' }) {
  const reduceMotion = useReducedMotion()
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], [from, to])
  return (
    <motion.div ref={ref} className={className} style={reduceMotion ? undefined : { y }}>
      {children}
    </motion.div>
  )
}

function Eyebrow({ children }) {
  return (
    <p className="font-m1-mono text-[12px] uppercase tracking-[0.2em] text-m1-muted">{children}</p>
  )
}

function SectionTitle({ children }) {
  return (
    <h2 className="mt-4 font-m1-display text-[clamp(36px,4.6vw,64px)] font-medium leading-[1.04] tracking-[-0.015em] text-m1-ink sm:font-normal">
      {children}
    </h2>
  )
}

const NOTE_CLASS =
  'max-w-md border border-m1-line bg-m1-ground/80 px-4 py-3 text-[14px] font-light leading-relaxed text-m1-ink-soft'

/** Numbered steps for browsers that cannot open an install window (Safari, Firefox). */
function InstallGuide({ guide }) {
  const reduceMotion = useReducedMotion()
  const ref = useRef(null)

  // On a phone the panel opens below the fold, so bring it into view.
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' })
  }, [reduceMotion])

  return (
    <div
      ref={ref}
      role="region"
      aria-label={guide.title}
      className="max-w-md border border-m1-line bg-m1-ground/85 p-5 backdrop-blur-md"
    >
      <h3 className="font-m1-mono text-[11px] uppercase tracking-[0.14em] text-m1-muted">
        {guide.title}
      </h3>
      <ol className="mt-5 space-y-4">
        {guide.steps.map((step, index) => (
          <li key={step} className="flex gap-4 text-[15px] font-light text-m1-ink-soft">
            <span className="font-m1-mono text-[12px] text-m1-muted">0{index + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      {guide.note && (
        <p className="mt-5 border-t border-m1-line-soft pt-4 text-[13px] font-light text-m1-muted">
          {guide.note}
        </p>
      )}
    </div>
  )
}

/**
 * "Download the app" page: an opening scene with the one-click install, the app in a desktop
 * window with what installing gives you, and the phone steps with a QR code beside the app on a
 * phone, over a dimmed photo of the finished cases.
 */
export default function DownloadApp() {
  const { installed, installedBefore, installPromptAvailable } = usePwa()
  const [message, setMessage] = useState(null)
  const [guideOpen, setGuideOpen] = useState(false)
  const shareUrl = useMemo(() => getShareUrl(), [])
  const guide = useMemo(() => getInstallGuide(), [])

  const handleInstall = async () => {
    if (installPromptAvailable) {
      const outcome = await promptInstall()
      setGuideOpen(false)
      setMessage(
        outcome === 'dismissed'
          ? 'Installation cancelled. Reload this page (Cmd or Ctrl + R) to get the install window again.'
          : null
      )
      return
    }
    setMessage(null)
    setGuideOpen((open) => !open)
  }

  const showGuide = guideOpen && !installPromptAvailable && guide
  const buttonLabel = installPromptAvailable
    ? 'Install on this computer'
    : guide?.buttonLabel || 'Install on this computer'

  return (
    <>
      <DownloadHero
        notices={
          <>
            {!installed && !installPromptAvailable && installedBefore && (
              <p className={NOTE_CLASS}>
                M1NT looks installed on this computer already, so the browser does not offer the
                install window again. Open it from your Dock, Launchpad or Start menu, or from the
                icon at the right end of the address bar.
              </p>
            )}
            {!installed && showGuide && <InstallGuide guide={guide} />}
            {message && (
              <p role="status" className={NOTE_CLASS}>
                {message}
              </p>
            )}
          </>
        }
      >
        {installed ? (
          <p className="font-m1-mono text-[12px] uppercase tracking-[0.14em] text-m1-muted">
            M1NT is already installed on this device
          </p>
        ) : (
          <M1ntButton
            solid
            onClick={handleInstall}
            aria-expanded={installPromptAvailable ? undefined : Boolean(showGuide)}
            className="min-h-[52px] px-8 text-[15px]"
          >
            <i className="ri-download-2-line text-lg" aria-hidden="true" />
            {buttonLabel}
          </M1ntButton>
        )}
      </DownloadHero>

      <section
        id="computer"
        className="relative scroll-mt-[calc(56px+env(safe-area-inset-top,0px))] border-t border-m1-line-soft py-[var(--section)]"
      >
        <div className="mx-auto grid max-w-[1200px] items-center gap-14 px-[var(--gutter)] lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
          <Reveal>
            <Eyebrow>Computer</Eyebrow>
            <SectionTitle>Opens like any other app.</SectionTitle>
            <p className="mt-6 max-w-[26em] text-[19px] font-light leading-relaxed text-m1-ink-soft">
              Install M1NT on Windows, macOS or Linux and it lives in your dock or taskbar.
            </p>
            <ul className="mt-10">
              {COMPUTER_FEATURES.map((feature, index) => (
                <li
                  key={feature.title}
                  className="grid grid-cols-[40px_1fr] border-t border-m1-line py-5"
                >
                  <span className="pt-0.5 font-m1-mono text-[12px] text-m1-muted">
                    0{index + 1}
                  </span>
                  <div>
                    <h3 className="text-[18px] text-m1-ink">{feature.title}</h3>
                    <p className="mt-1.5 text-[15px] font-light leading-relaxed text-m1-muted">
                      {feature.text}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15}>
            <Drift from={28} to={-28}>
              <AppWindow
                src="/screenshots/owner-wide.png"
                alt="The M1NT owner portal running in its own desktop window"
              />
            </Drift>
          </Reveal>
        </div>
      </section>

      <section
        id="phone"
        className="relative isolate scroll-mt-[calc(56px+env(safe-area-inset-top,0px))] overflow-hidden border-t border-m1-line-soft py-[var(--section)]"
      >
        <div
          className="m1nt-art-band pointer-events-none absolute inset-0 -z-10"
          aria-hidden="true"
        >
          <img
            src="/home/download-packs.jpg"
            width="1024"
            height="576"
            alt=""
            loading="lazy"
            decoding="async"
            draggable="false"
            className="m1nt-art-dim-deep h-full w-full select-none object-cover"
          />
          <div className="m1nt-grain absolute inset-0" />
          {/* Keeps the copy side calm: the photo only really shows behind the phone. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to right, rgba(10,9,13,0.92) 0%, rgba(10,9,13,0.62) 50%, rgba(10,9,13,0) 100%)',
            }}
          />
        </div>

        <div className="mx-auto grid max-w-[1200px] items-center gap-14 px-[var(--gutter)] lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-20">
          <Reveal>
            <Eyebrow>Phone and tablet</Eyebrow>
            <SectionTitle>Scan, tap, done.</SectionTitle>
            <p className="mt-6 max-w-[28em] text-[19px] font-light leading-relaxed text-m1-ink-soft">
              Point your camera at the code, then add M1NT to your home screen. It opens full
              screen, with no browser bars.
            </p>

            <div className="mt-10 grid gap-px border border-m1-line bg-m1-line backdrop-blur-md sm:grid-cols-[200px_1fr_1fr]">
              <div className="flex items-center gap-5 bg-m1-ground/85 p-5 sm:flex-col sm:items-start sm:gap-4">
                <div className="h-[120px] w-[120px] shrink-0 bg-white sm:h-[150px] sm:w-[150px]">
                  <QrCode value={shareUrl} label="QR code that opens the M1NT owner portal" />
                </div>
                <div>
                  <p className="font-m1-mono text-[11px] uppercase tracking-[0.14em] text-m1-muted">
                    Scan with your camera
                  </p>
                  <p className="mt-2 break-all font-m1-mono text-[12px] text-m1-ink-soft">
                    {shareUrl.replace(/^https?:\/\//, '')}
                  </p>
                </div>
              </div>

              {Object.entries(STEPS).map(([key, group]) => (
                <div key={key} className="bg-m1-ground/85 p-5">
                  <h3 className="font-m1-mono text-[11px] uppercase tracking-[0.14em] text-m1-muted">
                    {group.title}
                  </h3>
                  <ol className="mt-5 space-y-4">
                    {group.steps.map((step, index) => (
                      <li key={step} className="flex gap-4 text-[15px] font-light text-m1-ink-soft">
                        <span className="font-m1-mono text-[12px] text-m1-muted">0{index + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="flex justify-center lg:justify-end">
            <Drift from={32} to={-32} className="w-[min(280px,70vw)]">
              <PhoneFrame
                src="/screenshots/owner-narrow.png"
                alt="The M1NT owner portal on a phone"
              />
            </Drift>
          </Reveal>
        </div>
      </section>
    </>
  )
}
